import { ReactNode } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button, buttonVariants } from "@/components/ui/button";
import { EllipsisVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { PermissaoEnum } from "@workflow/types";
import { usePermissions } from "@/hooks/use-permissions";

export type ActionMenuItem = {
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;

  danger?: boolean;
  separator?: boolean;

  permissions?: PermissaoEnum[];
};

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Nome acessível do gatilho (ex.: "Mais opções para Fulano"), para leitor de tela. */
  ariaLabel?: string;
  /** Ajusta o gatilho (ex.: altura igual à do botão ao lado). */
  triggerClassName?: string;
}

export function ActionMenu({ items, ariaLabel, triggerClassName }: ActionMenuProps) {
  const { hasAnyPermission } = usePermissions();

  return (
    <Popover>
      <PopoverTrigger
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        className={buttonVariants({
          className: cn("p-0 data-[state=open]:bg-accent", triggerClassName),
          size: "xs",
          variant: "ghost",
        })}
      >
        <EllipsisVertical className="h-4 w-4 text-muted-foreground" />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="flex min-w-48 flex-col gap-1 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {items?.map((item, index) => {
          if (item?.permissions && !hasAnyPermission(item.permissions)) {
            return null;
          }

          if (item?.disabled) {
            return null;
          }

          if (item?.separator) {
            // MUDANÇA: border-t -> border-border
            return <div key={index} className="border-t border-border" />;
          }

          return (
            <button
              key={index}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick?.();
              }}
              className={cn(
                "flex w-full select-none items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                // MUDANÇA: hover:bg-gray-100 -> hover:bg-accent
                "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",

                // MUDANÇA: Tratamento de "Danger" para Dark Mode
                item.danger && [
                  "text-destructive", // Vermelho padrão
                  "hover:bg-destructive/10 hover:text-destructive", // Hover levemente avermelhado
                  "dark:text-red-400 dark:hover:bg-red-900/20", // Ajuste específico para legibilidade no dark
                ],
              )}
            >
              <div className="flex items-center gap-2">
                {item.icon}
                <span className="whitespace-nowrap">{item.label}</span>
              </div>

              {/* MUDANÇA: text-gray-500 -> text-muted-foreground */}
              {item.shortcut && (
                <span className="text-xs text-muted-foreground">{item.shortcut}</span>
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
